import { memo, useEffect, useMemo, useRef, useState } from '@teact';

import type { ApiMiniAppConversation } from '../../api/miniapp/types';

import { getAvatarLabel, getDisplayName } from '../../global/helpers/displayNames';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { getFirstLetters } from '../../util/textFormat';
import { formatConversationTime } from '../middle/helpers/dateFormat';

import styles from './ChatsHome.module.scss';

const AVATAR_TONES = ['blue', 'rose', 'green', 'orange', 'purple', 'teal'] as const;
const AVATAR_TONE_HASH_MULTIPLIER = 31;

type AvatarTone = typeof AVATAR_TONES[number];
type Sheet = 'compose' | 'story' | undefined;

const AVATAR_TONE_CLASS_NAMES: Record<AvatarTone, string> = {
  blue: styles.avatarToneBlue,
  rose: styles.avatarToneRose,
  green: styles.avatarToneGreen,
  orange: styles.avatarToneOrange,
  purple: styles.avatarTonePurple,
  teal: styles.avatarToneTeal,
};

type OwnProps = {
  conversations: ApiMiniAppConversation[];
  conversationsError: string | null;
  isLoading: boolean;
  activeChatId?: string;
  onOpenChat: (chatId: string) => void;
  onRetry: () => void;
};

function ChatsHome({
  conversations,
  conversationsError,
  isLoading,
  activeChatId,
  onOpenChat,
  onRetry,
}: OwnProps) {
  const lang = useLang();
  const searchInputRef = useRef<HTMLInputElement>();
  const [isEditing, setIsEditing] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  const [sheet, setSheet] = useState<Sheet>();

  const visibleConversations = useMemo(() => filterConversations(conversations, searchQuery), [conversations, searchQuery]);
  const selectedCount = selectedChatIds.length;

  useEffect(() => {
    if (isSearchActive) {
      searchInputRef.current?.focus();
    }
  }, [isSearchActive]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sheet) {
        setSheet(undefined);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sheet]);

  const handleToggleEditing = useLastCallback(() => {
    setIsEditing((wasEditing) => !wasEditing);
    setSelectedChatIds([]);
    setSearchQuery('');
    setIsSearchActive(false);
  });
  const handleActivateSearch = useLastCallback(() => setIsSearchActive(true));
  const handleCancelSearch = useLastCallback(() => {
    setSearchQuery('');
    setIsSearchActive(false);
  });
  const handleSearchKeyDown = useLastCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelSearch();
    }
  });
  const handleToggleChatSelection = useLastCallback((chatId: string) => {
    setSelectedChatIds((currentChatIds) => (
      currentChatIds.includes(chatId)
        ? currentChatIds.filter((currentChatId) => currentChatId !== chatId)
        : [...currentChatIds, chatId]
    ));
  });
  const handleConversationPress = useLastCallback((chatId: string) => {
    if (isEditing) {
      handleToggleChatSelection(chatId);
      return;
    }

    onOpenChat(chatId);
  });
  const handleComposeSelection = useLastCallback((chatId: string) => {
    setSheet(undefined);
    onOpenChat(chatId);
  });
  const handleCloseSheet = useLastCallback(() => setSheet(undefined));

  return (
    <section className={styles.root} aria-label={lang('Chats')}>
      <header className={styles.header}>
        <button
          type="button"
          className={`${styles.editButton}${isEditing ? ` ${styles.editButtonDone}` : ''}`}
          aria-label={isEditing ? lang('Done') : lang('Edit')}
          onClick={handleToggleEditing}
        >
          {isEditing ? lang('Done') : lang('Edit')}
        </button>
        <h1 className={styles.title}>{isEditing ? selectedCount : lang('Chats')}</h1>
        {!isEditing && (
          <div className={styles.rightActions}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={lang('AttachStory')}
              onClick={() => setSheet('story')}
            >
              <AddStoryIcon />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={lang('NewMessageTitle')}
              onClick={() => setSheet('compose')}
            >
              <ComposeIcon />
            </button>
          </div>
        )}
      </header>

      <div className={styles.searchRegion}>
        {!isSearchActive ? (
          <button type="button" className={styles.searchPill} aria-label={lang('Search')} onClick={handleActivateSearch}>
            <span className={styles.searchIdleContent}>
              <SearchIcon />
              <span className={styles.searchPlaceholder}>{lang('Search')}</span>
            </span>
          </button>
        ) : (
          <div className={styles.searchActiveRow}>
            <div className={styles.searchPill}>
              <SearchIcon />
              <input
                ref={searchInputRef}
                type="search"
                className={styles.searchInput}
                value={searchQuery}
                aria-label={lang('Search')}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.clearSearchButton}
                  aria-label={lang('Clear')}
                  onClick={() => setSearchQuery('')}
                >
                  <ClearIcon />
                </button>
              )}
            </div>
            <button type="button" className={styles.cancelButton} onClick={handleCancelSearch}>{lang('Cancel')}</button>
          </div>
        )}
      </div>

      <div className={styles.list} role="list" aria-label={lang('Chats')}>
        {visibleConversations.map((conversation) => {
          const viewModel = buildConversationRowViewModel(conversation);
          return (
            <ConversationRow
              key={viewModel.chatId}
              conversation={viewModel}
              isActive={activeChatId === viewModel.chatId}
              isEditing={isEditing}
              isSelected={selectedChatIds.includes(viewModel.chatId)}
              onPress={handleConversationPress}
            />
          );
        })}
        {isSearchActive && searchQuery && visibleConversations.length === 0 && !conversationsError && (
          <div className={styles.emptyState}>
            <div className={styles.stateTitle}>{lang('ChatListSearchNoResults')}</div>
            <div className={styles.stateDescription}>{lang('ChatListSearchNoResultsDescription')}</div>
          </div>
        )}
        {conversationsError && (
          <div className={styles.errorState} role="alert">
            <div className={styles.stateTitle}>会话加载失败</div>
            <div className={styles.errorDetail}>{conversationsError}</div>
            <button type="button" className={styles.retryButton} onClick={onRetry}>重试</button>
          </div>
        )}
        {!conversationsError && !isLoading && conversations.length === 0 && (
          <div className={styles.emptyState}>暂无会话</div>
        )}
        {!conversationsError && isLoading && conversations.length === 0 && (
          <div className={styles.emptyState}>加载中…</div>
        )}
      </div>

      {sheet && (
        <BottomSheet
          sheet={sheet}
          conversations={conversations}
          lang={lang}
          onClose={handleCloseSheet}
          onSelectConversation={handleComposeSelection}
        />
      )}
    </section>
  );
}

type ConversationRowViewModel = {
  chatId: string;
  displayName: string;
  avatarLetters: string;
  avatarTone: AvatarTone;
  preview: string;
  timestamp?: string;
  unreadCount?: number;
};

type ConversationRowProps = {
  conversation: ConversationRowViewModel;
  isActive: boolean;
  isEditing: boolean;
  isSelected: boolean;
  onPress: (chatId: string) => void;
};

function ConversationRow({ conversation, isActive, isEditing, isSelected, onPress }: ConversationRowProps) {
  const {
    chatId,
    displayName,
    avatarLetters,
    avatarTone,
    preview,
    timestamp,
    unreadCount,
  } = conversation;

  return (
    <div className={styles.rowItem} role="listitem">
      <button
        type="button"
        className={`${styles.row}${isActive ? ` ${styles.rowActive}` : ''}${isEditing ? ` ${styles.rowEditing}` : ''}`}
        aria-label={displayName}
        aria-pressed={isEditing ? isSelected : undefined}
        onClick={() => onPress(chatId)}
      >
        {isEditing && <SelectionControl isSelected={isSelected} />}
        <ConversationAvatar letters={avatarLetters} tone={avatarTone} />
        <span className={styles.rowBody}>
          <span className={styles.primaryLine}>
            <span className={styles.name}>{displayName}</span>
            {timestamp && <ConversationTimestamp value={timestamp} />}
          </span>
          <span className={styles.secondaryLine}>
            <span className={styles.preview}>{preview}</span>
            {unreadCount && <UnreadBadge count={unreadCount} />}
          </span>
        </span>
      </button>
    </div>
  );
}

function BottomSheet({
  sheet,
  conversations,
  lang,
  onClose,
  onSelectConversation,
}: {
  sheet: Exclude<Sheet, undefined>;
  conversations: ApiMiniAppConversation[];
  lang: ReturnType<typeof useLang>;
  onClose: VoidFunction;
  onSelectConversation: (chatId: string) => void;
}) {
  const isCompose = sheet === 'compose';
  const title = isCompose ? lang('NewMessageTitle') : lang('AttachStory');

  return (
    <div className={styles.sheetBackdrop} role="presentation" onClick={onClose}>
      <section
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.sheetHandle} />
        <h2 className={styles.sheetTitle}>{title}</h2>
        {isCompose ? (
          <div className={styles.sheetList} role="list">
            {conversations.map((conversation) => (
              <button
                key={conversation.chat_id}
                type="button"
                className={styles.sheetConversation}
                role="listitem"
                onClick={() => onSelectConversation(conversation.chat_id)}
              >
                <ConversationAvatar
                  letters={getFirstLetters(getAvatarLabel(conversation.title))}
                  tone={getAvatarTone(conversation.chat_id)}
                />
                <span className={styles.sheetConversationName}>{getDisplayName(conversation.title)}</span>
              </button>
            ))}
            {!conversations.length && <div className={styles.sheetEmpty}>{lang('ChatListSearchNoResults')}</div>}
          </div>
        ) : (
          <div className={styles.storyUnavailable}>{lang('ActionUnsupportedTitle')}</div>
        )}
        <button type="button" className={styles.sheetCancelButton} onClick={onClose}>{lang('Cancel')}</button>
      </section>
    </div>
  );
}

function ConversationAvatar({ letters, tone }: { letters: string; tone: AvatarTone }) {
  return <span className={`${styles.avatar} ${AVATAR_TONE_CLASS_NAMES[tone]}`} aria-hidden="true">{letters}</span>;
}

function ConversationTimestamp({ value }: { value: string }) {
  return <time className={styles.time}>{value}</time>;
}

function SelectionControl({ isSelected }: { isSelected: boolean }) {
  return <span className={`${styles.selectionControl}${isSelected ? ` ${styles.selectionControlSelected}` : ''}`} aria-hidden="true">
    {isSelected && <SelectionCheckIcon />}
  </span>;
}

function UnreadBadge({ count }: { count: number }) {
  return <span className={styles.unreadBadge}>{count}</span>;
}

function SearchIcon() {
  return (
    <svg className={styles.searchIcon} viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m12.6 12.6 4 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg className={styles.clearIcon} viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="8" fill="currentColor" />
      <path d="m7.2 7.2 5.6 5.6m0-5.6-5.6 5.6" fill="none" stroke="var(--color-background)" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function AddStoryIcon() {
  return (
    <svg className={styles.headerIcon} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v8m-4-4h8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function ComposeIcon() {
  return (
    <svg className={styles.headerIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.5 5.5h8.2M5.5 5.5v13h13v-7.9" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
      <path d="m11 14.5 1.1-3.5 6.8-6.8a1.5 1.5 0 0 1 2.1 2.1L14.2 13l-3.2 1.5Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function SelectionCheckIcon() {
  return (
    <svg className={styles.selectionCheckIcon} viewBox="0 0 18 18" aria-hidden="true">
      <path d="m4 9.2 3.1 3.1L14 5.6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function buildConversationRowViewModel(conversation: ApiMiniAppConversation): ConversationRowViewModel {
  const {
    chat_id: chatId,
    title,
    last_message_preview: preview,
    last_message_at: lastMessageAt,
    unread_count: unreadCount,
  } = conversation;
  const hasUnread = unreadCount !== null && unreadCount > 0;

  return {
    chatId,
    displayName: getDisplayName(title),
    avatarLetters: getFirstLetters(getAvatarLabel(title)),
    avatarTone: getAvatarTone(chatId),
    preview,
    timestamp: lastMessageAt ? formatConversationTime(lastMessageAt) : undefined,
    unreadCount: hasUnread ? unreadCount : undefined,
  };
}

function filterConversations(conversations: ApiMiniAppConversation[], searchQuery: string) {
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  if (!normalizedQuery) return conversations;

  return conversations.filter((conversation) => {
    const displayName = getDisplayName(conversation.title).toLocaleLowerCase();
    const preview = conversation.last_message_preview.toLocaleLowerCase();
    return displayName.includes(normalizedQuery) || preview.includes(normalizedQuery);
  });
}

function getAvatarTone(chatId: string): AvatarTone {
  let hash = 0;

  for (let index = 0; index < chatId.length; index++) {
    hash = (hash * AVATAR_TONE_HASH_MULTIPLIER + chatId.charCodeAt(index)) >>> 0;
  }

  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

export default memo(ChatsHome);
